"use client";

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStore } from "jotai";
import { useRouter } from "next/navigation";
import { getToolName, isToolUIPart, type UIMessage } from "ai";

import { invalidateForTool } from "./tool-invalidations";
import {
  useCloseBlock,
  useCurrentPage,
} from "@/components/Layouts/ToolLayout/currentTool";

import {
  TOOL_HANDLERS,
  chatMaybeDeletedBy,
  deletedToolId,
  pageLeftBy,
} from "./tool-handlers";
import { ConversationsService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import type { CodeModeCall } from "@/components/NewAndIncredibleChatWidget/noxus-message-types";
import { isRecord } from "@/utils/isRecord";

// The tools a code mode script called, each with a key of its own.
function scriptCalls(
  part: UIMessage["parts"][number]
): { key: string; call: CodeModeCall }[] {
  if (part.type !== "data-code_mode_calls" || !("data" in part)) return [];
  const data = part.data;
  if (!isRecord(data) || !Array.isArray(data.calls)) return [];
  const scriptId = "id" in part ? String(part.id) : "";
  return (data.calls as CodeModeCall[]).map((call) => ({
    key: `${scriptId}:${call.tool_call_id}`,
    call,
  }));
}

// A delete's close must fire at most once per conversation. A conversation
// whose run never terminates cleanly re-streams its completed tool calls on
// every resume/refresh; without cross-mount memory that would close a tool
// Genie deleted and then restored (DEV-1441). sessionStorage survives
// refresh within the tab and clears when it closes.
function sessionSet(prefix: string, conversationId: string | undefined) {
  const key = conversationId ? `${prefix}:${conversationId}` : null;
  const available = key !== null && typeof window !== "undefined";
  const read = (): string[] => {
    if (!available) return [];
    try {
      const raw = window.sessionStorage.getItem(key);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  };
  return {
    has: (value: string) => read().includes(value),
    add: (value: string) => {
      if (!available) return;
      const values = read();
      if (values.includes(value)) return;
      try {
        window.sessionStorage.setItem(key, JSON.stringify([...values, value]));
      } catch {
        // sessionStorage unavailable (private mode / quota) - dedup best-effort.
      }
    },
  };
}

// The tool calls a message carries that already completed, each by the key
// its effects are recorded under.
function completedKeys(message: UIMessage): string[] {
  return message.parts.flatMap((part) => [
    ...(isToolUIPart(part) && part.state === "output-available"
      ? [part.toolCallId]
      : []),
    ...scriptCalls(part).map(({ key }) => key),
  ]);
}

// ``navigate`` is the Genie panel's: it leaves a page whose subject a tool
// deleted. Elsewhere (an agent's test chat, a deployed app) the chat is the
// page, and leaving it strands the next approval card. No tool moves the page
// to what it changed: its row links there.
export function useToolEffects(
  messages: UIMessage[],
  conversationId?: string,
  { navigate = false }: { navigate?: boolean } = {}
): void {
  const queryClient = useQueryClient();
  const store = useStore();
  const router = useRouter();
  const { spotGroup } = useSpotUser();
  const groupId = spotGroup.id;
  const close = useCloseBlock();
  const { id: currentId } = useCurrentPage();
  const processed = useRef<Set<string>>(new Set());
  const shown = useRef<Set<string> | null>(null);
  const closeAfterOthers = useRef<string | null>(null);

  useEffect(() => {
    // Messages loaded from the server are history: the first render's, and a
    // page that arrives above a message already shown (an earlier page, or
    // the newest page swapped in after a compaction). Their tool calls are
    // marked processed without running effects, so a refresh or a page load
    // replays no navigation, close or other side effect.
    const seen = shown.current;
    const lastSeen = seen
      ? messages.findLastIndex((message) => seen.has(message.id))
      : messages.length - 1;
    shown.current = new Set([
      ...(seen ?? []),
      ...messages.map((message) => message.id),
    ]);
    messages.forEach((message, i) => {
      if (i <= lastSeen && !seen?.has(message.id))
        for (const key of completedKeys(message)) processed.current.add(key);
    });

    const deleted = new Set<string>();
    const closed = sessionSet("genie-close", conversationId);
    const apply = (
      key: string,
      toolName: string,
      input: unknown,
      output: unknown
    ) => {
      invalidateForTool(queryClient, toolName);
      TOOL_HANDLERS[toolName]?.(output, { queryClient, store, input });
      const id = deletedToolId(toolName, input, output);
      if (id && !closed.has(key)) {
        closed.add(key);
        deleted.add(id);
      }
      const left =
        navigate && !closed.has(key)
          ? pageLeftBy(toolName, input, output, new URL(window.location.href))
          : undefined;
      if (left) {
        closed.add(key);
        router.replace(left);
      }
      const maybe =
        navigate && !closed.has(key) && output !== undefined
          ? chatMaybeDeletedBy(toolName, input, new URL(window.location.href))
          : undefined;
      if (maybe) {
        closed.add(key);
        ConversationsService.getConversation({
          path: { group_id: groupId, conversation_id: maybe.conversationId },
          query: { paged: true },
        }).catch((error: { status?: number }) => {
          if (error.status === 404) router.replace(maybe.leaveTo);
        });
      }
    };
    for (const message of messages) {
      for (const part of message.parts) {
        // A tool a script called does what the same tool called on its own
        // does.
        for (const { key, call } of scriptCalls(part)) {
          if (processed.current.has(key)) continue;
          processed.current.add(key);
          if (!call.failed) apply(key, call.tool_name, call.input, call.output);
        }
        if (!isToolUIPart(part)) continue;
        if (part.state !== "output-available") continue;
        if (processed.current.has(part.toolCallId)) continue;
        processed.current.add(part.toolCallId);
        apply(part.toolCallId, getToolName(part), part.input, part.output);
      }
    }

    // A deleted tool leaves the open tools as its menu's Delete does; only
    // the Genie panel also moves the page off it. The tool on screen closes
    // last, once the others deleted with it have left the open tools, so the
    // page moves to a tool that still exists.
    if (closeAfterOthers.current) deleted.add(closeAfterOthers.current);
    closeAfterOthers.current = null;
    const onScreen = navigate ? currentId : null;
    for (const id of deleted)
      if (id !== onScreen) close(id, false, undefined, { deleted: true });
    if (onScreen && deleted.has(onScreen)) {
      if (deleted.size > 1) closeAfterOthers.current = onScreen;
      else close(onScreen, true, undefined, { deleted: true });
    }
  }, [
    messages,
    queryClient,
    store,
    router,
    conversationId,
    navigate,
    close,
    currentId,
    groupId,
  ]);
}
